import _get from 'lodash/get';
import { ALBUM_NAVIGATION_DIRECTIONS } from 'photobooks/constants/albumConstants';
import { getNewSpreadIndex, getNewDisabledDirection } from 'photobooks/utils/albumUtils';

const init = {
  busy: true,
  error: null,
  albumDetails: null,
  albumProperties: null,
  albumVariation: null,
  activeSpread: null,
  disabledDirection: null,
  slotUpdating: false,
  updatingSpreadIndexes: false,
  viewedSpreads: [],
  savingViewedSpreads: false,
};

const reducer = (state = init, action) => {
  switch (action.type) {
    case 'FETCH_ALBUM_ITEM':
      return {
        ...state,
        error: null,
      };
    case 'FETCH_ALBUM_ITEM_FULFILLED': {
      const newAlbumItem = action.payload[0] || {};
      const newActiveSpread = newAlbumItem.customization_data.spreads[0];
      return {
        ...state,
        albumItemUuid: newAlbumItem.uuid,
        albumDetails: newAlbumItem.customization_data,
        albumProperties: newAlbumItem.details,
        albumVariation: newAlbumItem.variation,
        activeSpread: newActiveSpread,
        viewedSpreads: [...state.viewedSpreads, newActiveSpread.spread_index],
        disabledDirection: ALBUM_NAVIGATION_DIRECTIONS.previous,
        busy: false,
      };
    }
    case 'FETCH_ALBUM_ITEM_REJECTED':
      return {
        ...state,
        busy: false,
        error: action.payload,
      };
    case 'FETCH_ALBUM_ITEM_BY_UUID':
      return {
        ...state,
        error: null,
      };
    case 'FETCH_ALBUM_ITEM_BY_UUID_FULFILLED': {
      const newAlbumItem = action.payload.albumItem || {};
      const activeSpreadIndex = action.payload.activeSpreadIndex || 0;
      return {
        ...state,
        albumItemUuid: newAlbumItem.uuid,
        albumDetails: newAlbumItem.customization_data,
        albumProperties: newAlbumItem.details,
        albumVariation: newAlbumItem.variation,
        activeSpread: newAlbumItem.customization_data.spreads[activeSpreadIndex],
        viewedSpreads: [...state.viewedSpreads, activeSpreadIndex],
        disabledDirection: ALBUM_NAVIGATION_DIRECTIONS.previous,
        busy: false,
      };
    }
    case 'FETCH_ALBUM_ITEM_BY_UUID_REJECTED':
      return {
        ...state,
        busy: false,
        error: action.payload,
      };
    case 'REQUEST_SLOT_UPDATE': {
      return {
        ...state,
        slotUpdating: true,
        error: null,
      };
    }
    case 'RECEIVE_SLOT_UPDATE': {
      const { activeSpread } = state;
      return {
        ...state,
        albumDetails: action.payload.customization_data,
        activeSpread: action.payload.customization_data.spreads[activeSpread.spread_index],
        slotUpdating: false,
      };
    }
    case 'REJECTED_SLOT_UPDATE': {
      return {
        ...state,
        slotUpdating: false,
        error: action.payload,
      };
    }
    case 'NAVIGATE_ACTIVE_SPREAD': {
      const navDirection = action.payload;
      const { activeSpread } = state;
      const totalSpreadLastIndex = state.albumDetails.spreads.length - 1;
      const newSpreadIndex = getNewSpreadIndex(
        activeSpread.spread_index,
        navDirection,
        totalSpreadLastIndex
      );
      const newDisabledDirection = getNewDisabledDirection(newSpreadIndex, totalSpreadLastIndex);
      const newActiveSpread = state.albumDetails.spreads[newSpreadIndex];
      return {
        ...state,
        activeSpread: newActiveSpread,
        disabledDirection: newDisabledDirection,
        viewedSpreads: [...state.viewedSpreads, newActiveSpread.spread_index],
      };
    }
    case 'UPDATE_ACTIVE_SPREAD': {
      const newSpreadNumber = action.payload;
      const totalSpreadLastIndex = state.albumDetails.spreads.length - 1;
      let newSpreadIndex = 0;
      const newActiveSpread = state.albumDetails.spreads.find((spread, index) => {
        if (spread.spread_index === newSpreadNumber) {
          newSpreadIndex = index;
        }
        return spread.spread_index === newSpreadNumber;
      });

      const newDisabledDirection = getNewDisabledDirection(newSpreadIndex, totalSpreadLastIndex);
      return {
        ...state,
        activeSpread: newActiveSpread,
        disabledDirection: newDisabledDirection,
        viewedSpreads: [...state.viewedSpreads, newActiveSpread.spread_index],
      };
    }
    case 'CLEAR_ALBUM': {
      return {
        ...state,
        busy: true,
        albumItemUuid: null,
        albumDetails: null,
        albumProperties: null,
        albumVariation: null,
        activeSpread: null,
        disabledDirection: null,
        viewedSpreads: [],
      };
    }
    case 'REQUEST_ADD_SPREAD': {
      return {
        ...state,
        updatingSpreadIndexes: true,
      };
    }
    case 'RECEIVE_ADD_SPREAD': {
      const { newActiveSpreadIndex, albumItem } = action.payload;
      const allSpreads = _get(albumItem, ['customization_data', 'spreads'], []);
      const totalSpreadLastIndex = allSpreads.length - 1;
      return {
        ...state,
        albumDetails: _get(albumItem, ['customization_data']) || {
          ...state.albumDetails,
        },
        activeSpread: allSpreads[newActiveSpreadIndex],
        disabledDirection: getNewDisabledDirection(newActiveSpreadIndex, totalSpreadLastIndex),
        updatingSpreadIndexes: false,
      };
    }
    case 'REJECTED_ADD_SPREAD': {
      return {
        ...state,
        updatingSpreadIndexes: false,
        error: action.payload,
      };
    }
    case 'REQUEST_REMOVE_SPREAD': {
      return {
        ...state,
        updatingSpreadIndexes: true,
      };
    }
    case 'RECEIVE_REMOVE_SPREAD': {
      const { newActiveSpreadIndex, albumItem } = action.payload;
      const allSpreads = _get(albumItem, ['customization_data', 'spreads'], []);
      const totalSpreadLastIndex = allSpreads.length - 1;
      return {
        ...state,
        albumDetails: _get(albumItem, ['customization_data']) || {
          ...state.albumDetails,
        },
        activeSpread: allSpreads[newActiveSpreadIndex],
        disabledDirection: getNewDisabledDirection(newActiveSpreadIndex, totalSpreadLastIndex),
        updatingSpreadIndexes: false,
      };
    }
    case 'REJECTED_REMOVE_SPREAD': {
      return {
        ...state,
        updatingSpreadIndexes: false,
        error: action.payload,
      };
    }
    case 'REQUEST_UPDATE_LAYOUT':
    case 'REQUEST_MOVE_SPREAD': {
      return {
        ...state,
        updatingSpreadIndexes: true,
      };
    }
    case 'RECEIVE_UPDATE_LAYOUT':
    case 'RECEIVE_MOVE_SPREAD': {
      const { newActiveSpreadIndex, albumItem } = action.payload;
      const allSpreads = _get(albumItem, ['customization_data', 'spreads'], []);
      const totalSpreadLastIndex = allSpreads.length - 1;
      return {
        ...state,
        albumDetails: _get(albumItem, ['customization_data']) || {
          ...state.albumDetails,
        },
        activeSpread: allSpreads[newActiveSpreadIndex],
        disabledDirection: getNewDisabledDirection(newActiveSpreadIndex, totalSpreadLastIndex),
        updatingSpreadIndexes: false,
      };
    }
    case 'REJECTED_UPDATE_LAYOUT':
    case 'REJECTED_MOVE_SPREAD': {
      return {
        ...state,
        updatingSpreadIndexes: false,
        error: action.payload,
      };
    }
    case 'REQUEST_SAVE_VIEWED_SPREADS': {
      return {
        ...state,
        savingViewedSpreads: true,
      };
    }
    case 'RECEIVE_SAVE_VIEWED_SPREADS': {
      return {
        ...state,
        viewedSpreads: [],
        savingViewedSpreads: false,
      };
    }
    case 'REJECTED_SAVE_VIEWED_SPREADS': {
      return {
        ...state,
        savingViewedSpreads: false,
        error: action.payload,
      };
    }
    default:
      return state;
  }
};

export default reducer;



// WEBPACK FOOTER //
// ./src/photobooks/reducers/albumItem/reducer.js